import { getSpaceId } from "shared/utils/space";
import { backendApi } from "./backend.api";
import { IService, ServiceType, StatusService } from "shared/models/models";

interface IServiceListQuery {
  date?: string | Date;
  user?: number;
  address?: number;
  statusId?: StatusService | StatusService[];
  spaceId?: number;
  type?: ServiceType;
}

export interface MultiService {
  date: Date;
  calibration?: string[];
  cleaning?: string[];
  list: string[];
  user: number;
  spaceId?: number;
}

export const serviceApi = backendApi.injectEndpoints({
  endpoints: (build) => {
    return {
      services: build.query<IService[], IServiceListQuery>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IServiceListQuery = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/service",
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Service" as const, id })),
                "Service",
              ]
            : ["Service"],
      }),
      getService: build.query<IService, number>({
        query: (id) => ({ url: `/service/${id}`, method: "GET" }),
        transformErrorResponse: (response: { route: string | number }) =>
          response.route,
        providesTags: (item) => [{ type: "Service", id: item?.id }],
      }),
      getNextService: build.query<IService, string>({
        query: (id) => ({ url: `/service/next/${id}`, method: "GET" }),
        providesTags: (item) => [{ type: "Service", id: item?.id }],
      }),
      getPrevService: build.query<IService, string>({
        query: (id) => ({ url: `/service/prev/${id}`, method: "GET" }),
        providesTags: (item) => [{ type: "Service", id: item?.id }],
      }),
      addService: build.mutation<IService, Partial<IService>>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: Partial<IService> = { ...payload };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/service",
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: () => ["Service"],
        transformResponse: (response: { data: IService }) => response.data,
      }),
      addMultiService: build.mutation<IService[], MultiService>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: MultiService = { ...payload };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/service/add-multi",
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: ["Service"],
      }),
      updateService: build.mutation<IService, Partial<IService>>({
        query: (payload) => ({
          url: `/service/${payload.id}`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [{ type: "Service", id: item?.id }],
      }),
      updateMultiService: build.mutation<IService[], IService[]>({
        query: (payload) => ({
          url: "/service/update-multi",
          method: "POST",
          data: payload,
        }),
        invalidatesTags: (result = []) => [
          ...result.map(({ id }) => ({ type: "Service", id: id }) as const),
        ],
      }),
      deleteService: build.mutation<IService, number>({
        query: (id) => ({
          url: `/service/${id}`,
          method: "DELETE",
          credentials: "include",
        }),
        invalidatesTags: ["Service"],
      }),
    };
  },
});

export const {
  useServicesQuery,
  useAddServiceMutation,
  useDeleteServiceMutation,
  useGetServiceQuery,
  useUpdateServiceMutation,
  useAddMultiServiceMutation,
  useGetNextServiceQuery,
  useGetPrevServiceQuery,
  useUpdateMultiServiceMutation,
} = serviceApi;
